// IMPORTANT: Import Sentry instrumentation first, before any other imports
import "./instrument";
import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { PostHogProvider } from "posthog-js/react";
import { PostHogUserIdentifier } from "./providers/PostHogUserIdentifier";
import { RedditPixelIdentifier } from "./providers/RedditPixelIdentifier";
import { TikTokPixelIdentifier } from "./providers/TikTokPixelIdentifier";
import { AppSettingsProvider } from "./context/AppSettingsContext";
import { GlobalStyles, ThemeProvider } from "@mui/material";
import { SnackbarProvider } from "notistack";
import { useAppSettings } from "./context/AppSettingsContext";
import { lightTheme, darkTheme } from "./theme/insightTheme";
import {
  installNativeLinkInterceptor,
  installPushTapRouting,
  installUniversalLinkHandler,
  isNative,
} from "./lib/platform";
import { initAppsFlyer } from "./lib/appsflyer";
import {
  BOTTOM_TAB_BAR_HEIGHT,
  BOTTOM_TAB_BAR_SAFE_AREA,
} from "./components/Native/bottomTabBarLayout";
import App, { router } from "./App";
import "./index.css";

const navigateInApp = (path: string) => {
  void router.navigate(path);
};

// Native app: external links open in the in-app browser sheet, and links to
// our own web origin are routed in-app instead (no-op on web)
installNativeLinkInterceptor(navigateInApp);

// Native app: Universal Links and push taps open inside the app. Module scope
// so the link or tap that LAUNCHED the app is caught before any screen renders
// (no-ops on web)
installUniversalLinkHandler(navigateInApp);
installPushTapRouting(navigateInApp);

// Self-heal the native-app class (normally set by the index.html head script).
// After iOS jetsams the WKWebView content process, Capacitor restore-reloads
// the page and the injected bridge script can run late — the head script then
// misdetects web while isNative() (message-handler check) is still correct.
// Without the class, long-press text selection re-enables and swallows taps.
if (isNative()) {
  document.documentElement.classList.add("native-app");
}

// Native app: start install attribution (no-op on web). Module scope, not an
// effect — StrictMode double-invokes effects, and the ATT wait window must
// open before any screen can be tapped.
initAppsFlyer();

// App-wide query defaults. Without these, every hook that omits staleTime
// inherits staleTime 0 + refetch-on-focus + 3 retries — on native that meant
// ~60 requests re-fired on every back-navigation and a refetch stampede each
// time the app foregrounded. Genuinely live hooks (quotes, market hours)
// opt back in with their own staleTime/refetchInterval.
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 10 * 60_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// Native: toasts sit above the tab bar and the home indicator, so an action
// in one (Undo) is never under either. Web keeps notistack's own placement.
const NATIVE_TOASTS = "native-toasts";
const nativeToastClasses = {
  containerAnchorOriginBottomLeft: NATIVE_TOASTS,
  containerAnchorOriginBottomCenter: NATIVE_TOASTS,
  containerAnchorOriginBottomRight: NATIVE_TOASTS,
};
const nativeToastStyles = {
  [`html .${NATIVE_TOASTS}`]: {
    bottom: `calc(${BOTTOM_TAB_BAR_HEIGHT + 8}px + ${BOTTOM_TAB_BAR_SAFE_AREA})`,
  },
};

const AppWithTheme = () => {
  const { isDarkMode } = useAppSettings();
  const theme = isDarkMode ? darkTheme : lightTheme;

  // Only track in production. Staging and local builds must not pollute the
  // production PostHog project, so only the build that receives
  // REACT_APP_ANALYTICS_ENV='production' mounts the provider. Without it,
  // usePostHog() returns undefined and every capture safely no-ops.
  const analyticsEnabled = process.env.REACT_APP_ANALYTICS_ENV === "production";

  // Cookie persistence is unreliable under the native WebView's custom
  // scheme; localStorage alone is stable there. Web keeps the shared
  // localStorage+cookie identity with the landing site.
  const posthogPersistence = isNative() ? "localStorage" : "localStorage+cookie";

  const appTree = (
    <PostHogUserIdentifier>
      <RedditPixelIdentifier>
        <TikTokPixelIdentifier>
          <App />
        </TikTokPixelIdentifier>
      </RedditPixelIdentifier>
    </PostHogUserIdentifier>
  );

  const toastClasses = isNative() ? nativeToastClasses : undefined;

  return (
    <ThemeProvider theme={theme}>
      {isNative() && <GlobalStyles styles={nativeToastStyles} />}
      <SnackbarProvider
        maxSnack={3}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        dense
        preventDuplicate
        classes={toastClasses}
      >
        {analyticsEnabled && (
          <PostHogProvider
            apiKey={process.env.REACT_APP_PUBLIC_POSTHOG_KEY ?? ""}
            options={{
              api_host:
                process.env.REACT_APP_PUBLIC_POSTHOG_HOST ??
                "https://eu.i.posthog.com",
              capture_pageview: true,
              capture_pageleave: true,
              autocapture: true,
              persistence: posthogPersistence,
            }}
          >
            {appTree}
          </PostHogProvider>
        )}
        {!analyticsEnabled && appTree}
      </SnackbarProvider>
    </ThemeProvider>
  );
};

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement,
);

root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <AppSettingsProvider>
        <AppWithTheme />
      </AppSettingsProvider>
    </QueryClientProvider>
  </React.StrictMode>,
);
